import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import APIContext from "../../../services/api/api";
import { URLS } from "../../../configuration/urls";
import {
  CircularProgress,
  MenuItem,
  Popover,
  TextField,
} from "@mui/material";
import { onShowToast } from "../../../components/toast";
import { useDebounce } from "use-debounce";
import CloseIcon from "@mui/icons-material/Close";
const KpiDropdownIcon = () => (
  <svg data-testid="mawb_dropdown_icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1l4 4 4-4" stroke="var(--tabs-functional-inactive-content-neutral)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const KpiSearchIcon = () => (
  <svg data-testid="mawb_search_icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M10.476 11.89a6 6 0 1 1 1.414-1.414l2.817 2.817a1 1 0 0 1-1.414 1.414l-2.816-2.816zM11 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" fill="var(--tabs-functional-inactive-content-neutral)"/>
  </svg>
);
import { FormattedMessage } from "react-intl";
import SegmentedToggle from "../../../components/SegmentedToggle";

const getMawbFromPath = (pathname) => {
  const match = pathname.match(/\/spot\/mawb\/(.+)/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export function RedirectionButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchAnchor, setSearchAnchor] = useState(null);
  const mawbTabRef = useRef(null);

  const isMawbKPI = location.pathname.startsWith("/spot/mawb/");
  const activeTab = isMawbKPI ? "mawb" : "overall";
  const selectedMawb = getMawbFromPath(location.pathname);

  const handleTabChange = (val) => {
    if (val === "overall") navigate("/spot");
    // "mawb" click opens the search popup instead of navigating directly
    if (val === "mawb") setSearchAnchor(mawbTabRef.current);
  };

  const mawbLabel = (
    <MawbTabLabel ref={mawbTabRef} aria-label="Search MAWB" data-testid="mawb_search_button">
      <MawbTabText>{selectedMawb || "MAWB KPIs"}</MawbTabText>
      <SearchIconButton component="span">
        {selectedMawb ? <KpiDropdownIcon /> : <KpiSearchIcon />}
      </SearchIconButton>
    </MawbTabLabel>
  );

  return (
    <Section>
      <ToggleWrapper>
        <SegmentedToggle
          variant="joined"
          ariaLabel="KPI view"
          value={activeTab}
          onChange={handleTabChange}
          options={[
            { value: "overall", label: "Overall KPIs", "data-testid": "overall_kpi_button" },
            { value: "mawb", label: mawbLabel, "data-testid": "mawb_kpi_button" },
          ]}
        />
      </ToggleWrapper>
      <MAWBSearchPopover
        anchorEl={searchAnchor}
        onClose={() => setSearchAnchor(null)}
      />
    </Section>
  );
}

let mawbCache = {
  items: [],
  cursor: null,
  hasMore: true,
};

function MAWBSearchPopover({ anchorEl, onClose }) {
  const navigate = useNavigate();
  const { showToast } = onShowToast();
  const location = useLocation();
  const { getData } = useContext(APIContext);

  const [data, setData] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [mawb, setMawb] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const [debouncedMAWB] = useDebounce(mawb, 400);
  const menuItemRefs = useRef([]);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    onClose();
    setHighlightedIndex(0);
  };

  const getCurrentMAWB = () => getMawbFromPath(location.pathname);

  const highlightMatch = (text, searchText) => {
    if (!searchText || !text) return <span>{text}</span>;
    const textStr = String(text);
    const searchStr = String(searchText).toLowerCase();
    const idx = textStr.toLowerCase().indexOf(searchStr);
    if (idx === -1) return <span>{text}</span>;
    return (
      <span>
        {textStr.substring(0, idx)}
        <strong>{textStr.substring(idx, idx + searchStr.length)}</strong>
        {textStr.substring(idx + searchStr.length)}
      </span>
    );
  };

  useEffect(() => {
    if (open) {
      setMawb(getCurrentMAWB());
      setHighlightedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    mawbCache = { items: [], cursor: null, hasMore: true };
    setData([]);
    setCursor(null);
    setHasMore(true);
    setHighlightedIndex(0);
    fetchMAWBList(null, true);
  }, [debouncedMAWB]);

  const fetchMAWBList = async (cursorValue, isInitial = false) => {
    if (loading || (!hasMore && !isInitial)) return;
    setLoading(true);
    try {
      const res = await getData(
        `h7/kpi/mawb?mawb=${debouncedMAWB}${cursorValue ? `&cursor=${cursorValue}` : ""}`,
        {},
        URLS.EC_CLEAR_CUSTOMS_BE()
      );
      const list = res?.data?.items || res?.data || [];
      const nextCursor = res?.cursor;
      const decodedList = list.map((item) => {
        try { return decodeURIComponent(item); } catch { return item; }
      });
      setData((prev) =>
        isInitial ? decodedList : [...new Set([...prev, ...decodedList])]
      );
      mawbCache.items = isInitial
        ? decodedList
        : [...new Set([...mawbCache.items, ...decodedList])];
      mawbCache.cursor = nextCursor;
      mawbCache.hasMore = !!nextCursor;
      setCursor(nextCursor);
      setHasMore(!!nextCursor);
    } catch (err) {
      console.error(err);
      showToast({ message: err });
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10;
    if (bottom && hasMore) fetchMAWBList(cursor);
  };

  const handleSelect = (value) => {
    handleClose();
    navigate(`/spot/mawb/${value}`);
  };

  const scrollToHighlighted = (index) => {
    menuItemRefs.current[index]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev < data.length - 1 ? prev + 1 : prev;
          scrollToHighlighted(next);
          return next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : 0;
          scrollToHighlighted(next);
          return next;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < data.length) {
          handleSelect(data[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          onScroll: handleScroll,
          sx: {
            mt: "6px",
            width: 300,
            maxHeight: 300,
            backgroundColor: "var(--button-subtle-bg)",
            border: "2px solid #e8ebef",
            borderRadius: "10px",
            overflow: "auto",
          },
        }}
      >
        <StickySearchBox onKeyDown={handleKeyDown}>
          <TextField
            fullWidth
            autoFocus
            variant="standard"
            placeholder="Type to Search"
            value={mawb}
            onChange={(e) => setMawb(e.target.value)}
            inputProps={{ autoComplete: "off" }}
            InputProps={{
              endAdornment: mawb && (
                <CloseIcon
                  sx={{ cursor: "pointer", fontSize: 18 }}
                  onClick={() => setMawb("")}
                />
              ),
            }}
            sx={{
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:after": { borderBottom: "none" },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
            }}
          />
        </StickySearchBox>

        {data.map((option, index) => (
          <MenuItem
            key={option}
            ref={(el) => (menuItemRefs.current[index] = el)}
            onClick={() => handleSelect(option)}
            sx={{
              textAlign: "center",
              color: "var(--text-primary)",
              backgroundColor:
                highlightedIndex === index
                  ? "var(--container-disabled) !important"
                  : "var(--container-dark)",
              "&.Mui-focusVisible": { backgroundColor: "var(--container-dark) !important" },
              "&.Mui-selected": { backgroundColor: "var(--container-dark)" },
            }}
          >
            {highlightMatch(option, mawb)}
          </MenuItem>
        ))}

        {loading && (
          <MenuItem disabled sx={{ justifyContent: "center" }}>
            <CircularProgress size={20} />
          </MenuItem>
        )}

        {!hasMore && !loading && (
          <MenuItem
            disabled
            sx={{
              justifyContent: "center",
              color: "var(--button-subtle-text)",
              backgroundColor: "var(--container-dark)",
              opacity: "1 !important",
              padding: "20px",
            }}
          >
            {data.length > 0 ? (
              <FormattedMessage id="NoMoreMAWBs" />
            ) : (
              <FormattedMessage id="NoResultsFound" />
            )}
          </MenuItem>
        )}
      </Popover>
    </>
  );
}

const Section = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

// SegmentedToggle hardcodes 24px height internally — override via wrapper.
const ToggleWrapper = styled.div`
  & > [role="group"] {
    width: 444px !important;
    height: 35px !important;
    min-height: 35px !important;
    max-height: 35px !important;
    border-radius: 4px !important;
  }

  & [role="group"] .MuiButtonBase-root {
    font-size: 14px !important;
    font-weight: 500 !important;
    font-family: Inter, sans-serif !important;
    line-height: 1.57 !important;
    border-radius: 3px !important;
  }
`;

// Label wrapper so the dropdown icon can be positioned at the right edge of the tab
const MawbTabLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
`;

// Truncates a long selected MAWB value instead of breaking the tab's fixed width
const MawbTabText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 24px);
`;

// Purely decorative icon positioned at the right edge of the tab; the whole tab is clickable
const SearchIconButton = styled.span`
  position: absolute;
  right: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StickySearchBox = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: var(--button-subtle-bg);
  padding: 8px 12px;
`;
